import * as React from 'react'
import { cn } from '@/lib/utils'
import { HugeiconsIcon } from "@hugeicons/react"
import { Loading01Icon } from "@hugeicons/core-free-icons"

/**
 * LoadingState - Standardized loading indicator
 * 
 * Provides consistent loading states across all pages.
 * Use this instead of ad-hoc loading spinners.
 * 
 * @example
 * ```tsx
 * {isLoading ? (
 *   <LoadingState message="Loading dashboard data..." />
 * ) : (
 *   <DashboardContent />
 * )}
 * ```
 */
interface LoadingStateProps {
  /** Optional loading message */
  message?: string
  /** Size variant */
  size?: 'sm' | 'md' | 'lg'
  /** Custom className */
  className?: string
  /** Full page loading (centers vertically) */
  fullPage?: boolean
}

export function LoadingState({
  message = 'Loading...',
  size = 'md',
  className,
  fullPage = false,
}: LoadingStateProps) {
  const iconSizes = {
    sm: 16,
    md: 24,
    lg: 32,
  } as const

  const textSizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  }

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3',
        fullPage && 'min-h-[400px]',
        className
      )}
    >
      <HugeiconsIcon
        icon={Loading01Icon}
        size={iconSizes[size]}
        className="animate-spin text-muted-foreground/60"
      />
      {message && (
        <p className={cn('text-muted-foreground/70', textSizeClasses[size])}>
          {message}
        </p>
      )}
    </div>
  )
}

